import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

export default function NotFound() {
  const navigate = useNavigate();
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Minimal particle canvas matching the site's neural aesthetic
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const particles = Array.from({ length: 55 }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      vx: (Math.random() - 0.5) * 0.3,
      vy: (Math.random() - 0.5) * 0.3,
      r: 0.8 + Math.random() * 1.6,
      accent: Math.random() < 0.2,
      opacity: 0.08 + Math.random() * 0.22,
    }));

    let raf: number;
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Connections
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const d = Math.hypot(particles[i].x - particles[j].x, particles[i].y - particles[j].y);
          if (d < 90) {
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = `rgba(167,176,164,${(1 - d / 90) * 0.07})`;
            ctx.lineWidth = 0.4;
            ctx.stroke();
          }
        }
      }

      // Particles
      particles.forEach(p => {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0) p.x = canvas.width;
        if (p.x > canvas.width) p.x = 0;
        if (p.y < 0) p.y = canvas.height;
        if (p.y > canvas.height) p.y = 0;

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = p.accent
          ? `rgba(47,107,79,${p.opacity})`
          : `rgba(167,176,164,${p.opacity * 0.6})`;
        ctx.fill();
      });

      raf = requestAnimationFrame(draw);
    };

    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <div className="relative min-h-screen bg-ink flex flex-col items-center justify-center overflow-hidden px-6">
      {/* Particle canvas */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 pointer-events-none"
        aria-hidden
      />

      {/* Ghost 404 */}
      <div
        className="absolute select-none pointer-events-none font-display font-black text-ink-3"
        style={{ fontSize: "clamp(12rem, 30vw, 28rem)", lineHeight: 1, opacity: 0.06, letterSpacing: "-0.05em" }}
        aria-hidden
      >
        404
      </div>

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center text-center max-w-lg">
        {/* Eyebrow */}
        <motion.div
          className="flex items-center gap-3 mb-8"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        >
          <span className="w-6 h-px bg-ink-4" />
          <span className="font-mono text-xs text-chalk-3 tracking-widest uppercase">DOO MADE</span>
          <span className="w-6 h-px bg-ink-4" />
        </motion.div>

        {/* Headline */}
        <motion.h1
          className="font-display font-bold text-chalk leading-tight mb-4"
          style={{ fontSize: "clamp(2.2rem, 5vw, 3.5rem)" }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        >
          Wrong signal.
        </motion.h1>

        {/* Subhead */}
        <motion.p
          className="text-chalk-2 font-light text-base lg:text-lg leading-relaxed mb-10"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        >
          This page does not exist — or it moved. Either way, the path you took led nowhere. Let&apos;s get you back on track.
        </motion.p>

        {/* CTA */}
        <motion.div
          className="flex flex-wrap gap-4 justify-center"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.32, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        >
          <button
            onClick={() => navigate("/")}
            className="px-8 py-4 bg-acid text-white font-semibold text-sm tracking-tight rounded-sm hover:bg-acid-hover transition-colors duration-200 cursor-pointer whitespace-nowrap group"
          >
            Back to Home
            <span className="inline-block ml-2 group-hover:translate-x-1 transition-transform duration-200">→</span>
          </button>
        </motion.div>

        {/* Mono path hint */}
        <motion.p
          className="mt-8 font-mono text-xs text-chalk-3 opacity-50"
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.5 }}
          transition={{ delay: 0.5, duration: 0.8 }}
        >
          signal lost at: {typeof window !== "undefined" ? window.location.pathname : ""}
        </motion.p>
      </div>

      {/* Bottom accent line */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-ink-4" />
    </div>
  );
}
